/* eslint-disable max-lines -- single-source hook for the redesigned chat data layer; composer + reply + edit + delete state plus the mark-as-unread latch all live here so panel and conversation page consume one surface */
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { ModalStatus, type UseModalState, useModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useState } from "react";

import { useChatChannel } from "../api/useChatChannel";
import { useChatMessages } from "../api/useChatMessages";
import { useDeleteMessage } from "../api/useDeleteMessage";
import { useEditTextMessage } from "../api/useEditTextMessage";
import { useEnsureChatChannel } from "../api/useEnsureChatChannel";
import { useSendFileMessage } from "../api/useSendFileMessage";
import { useSendTextMessage } from "../api/useSendTextMessage";
import { type Exclusion, useWorkerBlockedStatus } from "../api/useWorkerBlockedStatus";
import { useWorkerHasActiveChartingNoncompliance } from "../api/useWorkerHasActiveChartingNoncompliance";
import { createChannelUrl } from "../constants";
import { type ReplyContext, type TextChatMessage } from "../types";
import {
  logChatMessageDeleted,
  logChatMessageEdited,
  logChatMessageSent,
} from "../utils/logChatEvents";
import { useMarkAsUnreadHandlers } from "./useMarkAsUnreadHandlers";
import { useReplyState } from "./useReplyState";

/**
 * Context for an in-progress edit. The original Sendbird `data` is not
 * captured here: `updateUserMessage` uses merge semantics, so omitting `data`
 * on the update leaves the existing workplace metadata untouched.
 */
export interface EditingMessage {
  messageId: number;
  originalText: string;
}

interface UseChatPanelStateParams {
  workerId: string;
  workplaceId?: string;
  placementId?: string;
  /**
   * Pre-fetched GroupChannel from the caller (e.g. channel-list selection).
   * When provided, skips the internal `useChatChannel` fetch and the
   * `useEnsureChatChannel` mutation. When omitted, the hook fetches and
   * ensures the channel itself (panel + deep-link paths).
   */
  channel?: GroupChannel;
  /**
   * Optional modal state. When provided, `handleClose` closes the modal and
   * the panel unmounts on mark-unread (matching the existing `ChatPanel`
   * behavior). When omitted (page surface), `handleClose` only invokes
   * `onClose` and the internal latch suppresses auto-mark-read until the
   * conversation key changes.
   */
  modalState?: UseModalState;
  onClose?: () => void;
}

interface UseChatPanelStateResult {
  workplaceId: string;
  isAdmin: boolean;
  isWorkerSession: boolean;
  isLoading: boolean;
  isError: boolean;
  isBlocked: boolean;
  isSendingDisabled: boolean;
  messages: ReturnType<typeof useChatMessages>["messages"];
  hasMore: boolean;
  isLoadingMore: boolean;
  exclusion: Exclusion | undefined;
  replyTo: ReplyContext | null;
  editingMessage: EditingMessage | undefined;
  deleteModalState: UseModalState;
  isDeleting: boolean;
  handleClose: () => void;
  handleSend: (message: string) => void;
  handleFileSelect: (file: File) => void;
  handleRetry: () => void;
  handleMarkMessageAsUnread: (messageId: number) => void;
  handleMarkConversationAsUnread: () => void;
  canMarkConversationAsUnread: boolean;
  loadMore: () => void;
  handleReply: (context: ReplyContext) => void;
  clearReply: () => void;
  handleEdit: (message: TextChatMessage) => void;
  clearEditing: () => void;
  handleDelete: (message: TextChatMessage) => void;
  handleDeleteConfirm: () => void;
  handleDeleteCancel: () => void;
}

export function useChatPanelState(params: UseChatPanelStateParams): UseChatPanelStateResult {
  const {
    workerId,
    workplaceId: workplaceIdFromCaller,
    placementId,
    channel: channelProperty,
    modalState,
    onClose,
  } = params;
  const session = useSession();
  const workplaceId = workplaceIdFromCaller ?? session.profile.userId;
  const isAdmin = Boolean(session.admin);
  const isWorkerSession = session.type === "AGENT";

  const { replyTo, setReplyTo, clearReply } = useReplyState();

  const [editingMessage, setEditingMessage] = useState<EditingMessage | undefined>(undefined);
  const [messageToDelete, setMessageToDelete] = useState<TextChatMessage | undefined>(undefined);
  const deleteModalState = useModalState();
  // Stable setter reference for `useModalState` so callbacks/effects don't
  // re-run on every render (the rest of the modal-state object is recreated
  // each render).
  const setDeleteModalStatus = deleteModalState.setModalStatus;

  const clearEditing = useCallback(() => {
    setEditingMessage(undefined);
  }, []);

  const closeDeleteFlow = useCallback(() => {
    setMessageToDelete(undefined);
    setDeleteModalStatus(ModalStatus.CLOSED);
  }, [setDeleteModalStatus]);

  const {
    data: blockedStatus,
    isLoading: isLoadingBlockedStatus,
    isError: isErrorBlockedStatus,
    refetch: refetchBlockedStatus,
  } = useWorkerBlockedStatus({ workplaceId, workerId });

  const { hasActiveChartingNoncompliance, isLoading: isLoadingChartingNoncompliance } =
    useWorkerHasActiveChartingNoncompliance({ workplaceId, workerId });

  const { mutate: ensureChannel } = useEnsureChatChannel();

  const {
    data: fetchedChannel,
    isLoading: isLoadingChannel,
    isError: isErrorChannel,
    refetch: refetchChannel,
  } = useChatChannel({ workplaceId, workerId, placementId, enabled: !channelProperty });

  const channel = channelProperty ?? fetchedChannel ?? null;

  const baseUrl = createChannelUrl(workplaceId, workerId);
  const channelUrl = placementId ? `${baseUrl}_${placementId}` : baseUrl;

  // Once the user marks the conversation unread, suppress the auto-mark-read
  // effect so an incoming message doesn't silently re-mark it read while they
  // are still viewing it. Reset when the conversation key changes. Inert in
  // the panel path because the modal unmounts immediately on mark-unread.
  const [hasUserMarkedUnread, setHasUserMarkedUnread] = useState(false);
  useEffect(() => {
    setHasUserMarkedUnread(false);
    clearReply();
    clearEditing();
    closeDeleteFlow();
  }, [channelUrl, clearReply, clearEditing, closeDeleteFlow]);

  const {
    messages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
    hasMore,
    loadMore,
    isLoadingMore,
    refetch: refetchMessages,
  } = useChatMessages({
    channel,
    // Admins impersonate workplace/worker users, so calling `markAsRead` would
    // mark the impersonated user's read cursor — hiding the unread badge from
    // the actual user. Suppress the auto-mark for admin viewers on both sides
    // of the conversation.
    autoMarkAsReadEnabled: !hasUserMarkedUnread && !isAdmin,
  });

  const { mutate: sendMessage, isLoading: isSending } = useSendTextMessage();
  const { mutate: sendFile, isLoading: isSendingFile } = useSendFileMessage();
  const { mutate: editMessage, isLoading: isEditing } = useEditTextMessage();
  const { mutate: deleteMessage, isLoading: isDeleting } = useDeleteMessage();

  // Ensure the base channel exists when no pre-fetched channel was provided.
  // Channel-list selections already carry a channel; placement channels are
  // created server-side, so we only ensure the base workplaceId_workerId pair.
  useEffect(() => {
    if (!channelProperty && workplaceId && workerId) {
      ensureChannel({ workplaceId, workerId });
    }
  }, [channelProperty, ensureChannel, workplaceId, workerId]);

  const handleClose = useCallback(() => {
    modalState?.closeModal();
    onClose?.();
  }, [modalState, onClose]);

  const handleSend = useCallback(
    (message: string) => {
      if (!channel) {
        return;
      }
      if (editingMessage) {
        editMessage(
          {
            channel,
            messageId: editingMessage.messageId,
            message,
          },
          {
            onSuccess: () => {
              logChatMessageEdited({ workerId, workplaceId });
              clearEditing();
            },
          }
        );
        return;
      }
      sendMessage(
        { channel, message, parentMessageId: replyTo?.messageId },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "text",
              isReply: Boolean(replyTo),
            });
            clearReply();
          },
        }
      );
    },
    [
      channel,
      editingMessage,
      editMessage,
      clearEditing,
      sendMessage,
      replyTo,
      clearReply,
      workerId,
      workplaceId,
    ]
  );

  const handleReply = useCallback(
    (context: ReplyContext) => {
      clearEditing();
      closeDeleteFlow();
      setReplyTo(context);
    },
    [setReplyTo, clearEditing, closeDeleteFlow]
  );

  const handleEdit = useCallback(
    (message: TextChatMessage) => {
      clearReply();
      closeDeleteFlow();
      setEditingMessage({
        messageId: message.messageId,
        originalText: message.message,
      });
    },
    [clearReply, closeDeleteFlow]
  );

  const handleDelete = useCallback(
    (message: TextChatMessage) => {
      clearReply();
      clearEditing();
      setMessageToDelete(message);
      setDeleteModalStatus(ModalStatus.OPEN);
    },
    [clearReply, clearEditing, setDeleteModalStatus]
  );

  const handleDeleteCancel = useCallback(() => {
    // Backdrop click + Escape route through here too; ignore them while the
    // mutation is in flight so the user can't dismiss the dialog out from
    // under an already-firing delete.
    if (isDeleting) {
      return;
    }
    closeDeleteFlow();
  }, [closeDeleteFlow, isDeleting]);

  const handleDeleteConfirm = useCallback(() => {
    if (!channel || !messageToDelete?.sendbirdMessage) {
      return;
    }
    // Reply and edit are already cleared by `handleDelete` (mutual-exclusion
    // contract), so onSuccess just needs to log + close the dialog.
    deleteMessage(
      { channel, message: messageToDelete.sendbirdMessage },
      {
        onSuccess: () => {
          logChatMessageDeleted({ workerId, workplaceId });
          closeDeleteFlow();
        },
      }
    );
  }, [channel, deleteMessage, messageToDelete, closeDeleteFlow, workerId, workplaceId]);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!channel) {
        return;
      }
      sendFile(
        { channel, file },
        {
          onSuccess: () => {
            logChatMessageSent({
              workerId,
              workplaceId,
              messageType: "file",
              isReply: false,
            });
          },
        }
      );
    },
    [channel, sendFile, workerId, workplaceId]
  );

  const isLoading =
    (!channelProperty && isLoadingChannel) ||
    isLoadingMessages ||
    isLoadingBlockedStatus ||
    isLoadingChartingNoncompliance;
  // Charting-noncompliance errors graceful-degrade to `false`, which keeps the
  // block check strict — so a charting error doesn't surface as an `isError`
  // on the panel.
  const isError = (!channelProperty && isErrorChannel) || isErrorMessages || isErrorBlockedStatus;
  const isBlocked = (blockedStatus?.isBlocked ?? false) && !hasActiveChartingNoncompliance;

  const handleRetry = useCallback(() => {
    if (!channelProperty) {
      void refetchChannel();
    }
    void refetchBlockedStatus();
    void refetchMessages();
  }, [channelProperty, refetchChannel, refetchBlockedStatus, refetchMessages]);

  const afterMarkUnread = useCallback(() => {
    setHasUserMarkedUnread(true);
    modalState?.closeModal();
    onClose?.();
  }, [modalState, onClose]);

  const { handleMarkMessageAsUnread, handleMarkConversationAsUnread, canMarkConversationAsUnread } =
    useMarkAsUnreadHandlers({
      channel,
      workplaceId,
      workerId,
      messages,
      onAfterMark: afterMarkUnread,
    });

  return {
    workplaceId,
    isAdmin,
    isWorkerSession,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled: isSending || isSendingFile || isEditing || isDeleting || !channel,
    messages,
    hasMore,
    isLoadingMore,
    exclusion: blockedStatus?.exclusion,
    replyTo,
    editingMessage,
    deleteModalState,
    isDeleting,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    loadMore,
    handleReply,
    clearReply,
    handleEdit,
    clearEditing,
    handleDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
  };
}
/* eslint-enable max-lines */
